import { useEffect, useRef, useState } from "react";

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

export const useScrollAnimation = (options: UseScrollAnimationOptions = {}) => {
  const { threshold = 0.1, rootMargin = "0px", triggerOnce = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Fail-safe: if IntersectionObserver is unavailable, show immediately.
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    let cancelled = false;
    // Defer the layout read to the next frame so we don't force a synchronous
    // reflow during React's commit phase (multiple AnimatedSections mounting
    // at once would otherwise each trigger getBoundingClientRect back-to-back).
    let observer: IntersectionObserver | null = null;
    const rafId = window.requestAnimationFrame(() => {
      if (cancelled) return;
      const rect = element.getBoundingClientRect();
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < viewportH && rect.bottom > 0) {
        setIsVisible(true);
        if (triggerOnce) return;
      }
      observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
              observer?.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );
      observer.observe(element);
    });

    // Ultimate fallback — never leave content invisible for more than 1.5s.
    const fallback = window.setTimeout(() => setIsVisible(true), 1500);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
      observer?.disconnect();
      window.clearTimeout(fallback);
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
};

export default useScrollAnimation;
